import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Briefcase, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { useSession } from "@/context/Session";
import { errMsg } from "@/lib/api";
import { cnpjMask, validCnpj } from "@/lib/format";
import { Logo } from "@/components/brand/Logo";
import { CrudForm } from "@/components/app/CrudPage";

export const COMPANY_FIELDS = [
  { name: "name", label: "Razão social", required: true, wide: true }, { name: "trade_name", label: "Nome fantasia" },
  { name: "cnpj", label: "CNPJ", required: true, lookup: true, mask: "cnpj" }, { name: "regime", label: "Regime tributário", type: "select", options: ["MEI", "Simples Nacional", "Lucro Presumido", "Lucro Real", "A definir"] },
  { name: "cnae", label: "CNAE principal" }, { name: "activity", label: "Atividade" }, { name: "address", label: "Endereço", wide: true },
  { name: "city", label: "Cidade" }, { name: "uf", label: "UF" },
  { name: "email", label: "E-mail" }, { name: "phone", label: "Telefone" }, { name: "opening_date", label: "Data de abertura", type: "date" },
];
export const ORG_FIELDS = [
  { name: "name", label: "Nome da organização contábil", required: true, wide: true }, { name: "cnpj", label: "CNPJ", required: true, mask: "cnpj" },
  { name: "crc_number", label: "Registro CRC da organização", required: true, help: "Informar o CRC não significa verificação automática." },
  { name: "crc_uf", label: "Jurisdição (UF do CRC)", required: true }, { name: "email", label: "E-mail" }, { name: "phone", label: "Telefone" },
];

export const cnpjLookup = (api) => async (digits) => {
  try {
    const { data } = await api.get(`/cnpj/${digits}`);
    if (data.situacao && data.situacao !== "ATIVA") toast.warning(`Atenção: este CNPJ está com situação "${data.situacao}" na Receita Federal.`);
    else toast.success("Dados preenchidos pela Receita Federal. Confira antes de salvar.");
    return data;
  }
  catch (e) { toast.error(errMsg(e)); return null; }
};

export default function Onboarding() {
  const { api, reload, setCompanyId, setOrgId } = useSession();
  const [mode, setMode] = useState(null);
  const nav = useNavigate();
  const createCompany = async (d) => {
    if (!validCnpj(d.cnpj)) return toast.error("CNPJ inválido. Confira os dígitos.");
    try { const { data } = await api.post("/companies", { ...d, cnpj: cnpjMask(d.cnpj) }); setCompanyId(data.id); await reload(); nav("/app"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const createOrg = async (d) => {
    try { const { data } = await api.post("/orgs", d); setOrgId(data.id); await reload(); nav("/contador"); } catch (e) { toast.error(errMsg(e)); }
  };
  const demo = async () => {
    try { const { data } = await api.post("/companies/demo"); setCompanyId(data.id); await reload(); nav("/app"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="min-h-screen bg-[#F4F6F8] px-4 py-10">
      <div className="mx-auto max-w-3xl">
        <Logo size="text-3xl" />
        <h1 className="mt-8 text-3xl font-bold text-[#12343B]">Boas-vindas! Como você vai usar a Normah?</h1>
        {!mode && <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {[["empresa", Building2, "Sou empresário", "Cadastrar minha empresa"], ["org", Briefcase, "Sou contador parceiro", "Cadastrar organização contábil"], ["demo", PlayCircle, "Explorar com dados demo", "Empresa fictícia isolada"]].map(([k, I, t, d]) => (
            <button key={k} data-testid={`onboarding-${k}`} onClick={() => (k === "demo" ? demo() : setMode(k))} className="card-soft p-6 text-left transition-transform hover:-translate-y-1">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#81D8D0]/30"><I className="h-5 w-5 text-[#12343B]" /></span>
              <p className="mt-4 font-bold text-[#12343B]">{t}</p><p className="text-sm text-[#587178]">{d}</p>
            </button>))}
        </div>}
        {mode && <div className="card-soft mt-8 p-6">
          <button className="mb-4 text-sm font-semibold text-[#2a8f86]" onClick={() => setMode(null)}>← Voltar</button>
          {mode === "empresa" ? <CrudForm fields={COMPANY_FIELDS} initial={{ regime: "Simples Nacional" }} onSubmit={createCompany} submitLabel="Cadastrar empresa" lookup={cnpjLookup(api)} />
            : <CrudForm fields={ORG_FIELDS} initial={{}} onSubmit={createOrg} submitLabel="Cadastrar organização" />}
        </div>}
      </div>
    </div>
  );
}
